import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import api, { API, errText } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Search, ShoppingBag, Plus, Minus, Loader2, Check, Trash2, ChevronLeft, ChevronRight, Images } from "lucide-react";
import { toast } from "sonner";

const eur = (n) => `€ ${(Number(n) || 0).toFixed(2)}`;
const norm = (s) => (s || "").toString().normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
const mediaUrl = (token, photo) => (photo ? (photo.startsWith("http") ? photo : `${API}/public/merch/${token}/media/${photo}`) : null);
const photoList = (token, p) => {
  const arr = (p.photos && p.photos.length ? p.photos : (p.photo ? [p.photo] : []));
  return arr.map((ph) => mediaUrl(token, ph)).filter(Boolean);
};

export default function PublicCollection() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [cart, setCart] = useState({}); // key -> {product, gender, size, color, qty}
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", phone: "" });
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [gallery, setGallery] = useState(null); // {name, photos:[], index}

  useEffect(() => {
    api.get(`/public/merch/${token}`).then((r) => setData(r.data)).catch(() => setErr(true));
  }, [token]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const tokens = norm(q).split(" ").filter(Boolean);
    return data.products.filter((p) => {
      if (catFilter !== "all" && p.category_id !== catFilter) return false;
      if (tokens.length === 0) return true;
      const hay = norm(`${p.name} ${p.description || ""}`);
      return tokens.every((t) => hay.includes(t));
    });
  }, [data, q, catFilter]);

  // Group by category (order set by manager) then products A→Z within.
  const sections = useMemo(() => {
    if (!data) return [];
    const cats = data.categories || [];
    const byAlpha = (a, b) => a.name.localeCompare(b.name, "it", { sensitivity: "base" });
    const secs = [];
    cats.forEach((c) => {
      const items = filtered.filter((p) => p.category_id === c.id).sort(byAlpha);
      if (items.length) secs.push({ id: c.id, name: c.name, items });
    });
    const uncat = filtered.filter((p) => !p.category_id || !cats.some((c) => c.id === p.category_id)).sort(byAlpha);
    if (uncat.length) secs.push({ id: "__none__", name: "Altro", items: uncat });
    return secs;
  }, [data, filtered]);

  const addToCart = (product, sel, qty = 1) => {
    const key = `${product.id}|${sel.gender || ""}|${sel.size || ""}|${sel.color || ""}`;
    setCart((c) => ({ ...c, [key]: { product, ...sel, qty: (c[key]?.qty || 0) + qty } }));
    toast.success("Aggiunto alla prenotazione");
  };
  const setQty = (key, qty) => setCart((c) => ({ ...c, [key]: { ...c[key], qty: Math.max(1, qty) } }));
  const removeItem = (key) => setCart((c) => { const n = { ...c }; delete n[key]; return n; });
  const cartItems = Object.entries(cart);
  const total = cartItems.reduce((s, [, it]) => s + it.product.price * it.qty, 0);

  const openGallery = (product, index = 0) => {
    const photos = photoList(token, product);
    if (photos.length) setGallery({ name: product.name, photos, index });
  };

  const submit = async () => {
    if (!form.first_name.trim() || !form.last_name.trim() || !form.phone.trim()) { toast.error("Nome, cognome e telefono sono obbligatori."); return; }
    const digits = form.phone.replace(/\D/g, "").replace(/^0039/, "").replace(/^39(?=\d{10}$)/, "");
    const plausible = digits.length >= 6 && digits.length <= 11 && (!digits.startsWith("3") || digits.length === 10);
    if (!plausible) { toast.error("Controlla il numero di cellulare: sembra incompleto o non valido."); return; }
    setBusy(true);
    try {
      const items = cartItems.map(([, it]) => ({ product_id: it.product.id, gender: it.gender || null, size: it.size || null, color: it.color || null, qty: it.qty }));
      const { data: res } = await api.post(`/public/merch/${token}/booking`, { ...form, note: note.trim() || null, items });
      setDone(res); setConfirmOpen(false); setCart({}); setForm({ first_name: "", last_name: "", phone: "" }); setNote("");
    } catch (e) { toast.error(errText(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  if (err) return <div className="min-h-screen flex items-center justify-center bg-[#FAF8F5] p-6"><p className="text-[#5F6B64] text-center" data-testid="public-collection-unavailable">Questa raccolta non è disponibile o è stata chiusa.</p></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-[#FAF8F5]"><Loader2 className="h-8 w-8 animate-spin text-[#1B3B2B]" /></div>;
  if (data.closed) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#FAF8F5] p-6 text-center" data-testid="public-collection-closed">
      <p className="text-[11px] uppercase tracking-wide text-[#C9A227]">{data.stable_name}</p>
      <h1 className="font-display text-2xl font-bold text-[#1B3B2B] mt-1">{data.public_title}</h1>
      <p className="text-[#5F6B64] mt-3">Le prenotazioni sono momentaneamente chiuse.</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAF8F5] pb-28" data-testid="public-collection-page">
      <header className="sticky top-0 z-30 bg-[#1B3B2B] text-[#FAF8F5] px-4 py-4 pt-safe">
        <p className="text-[11px] uppercase tracking-wide text-[#C9A227]">{data.stable_name}</p>
        <h1 className="font-display text-2xl font-bold" data-testid="public-collection-title">{data.public_title}</h1>
        {data.public_message && <p className="text-sm text-[#D9E2DA] mt-1">{data.public_message}</p>}
      </header>

      <div className="px-4 py-3 sticky top-[76px] z-20 bg-[#FAF8F5]/95 backdrop-blur border-b border-[#E5DEC9]">
        <div className="relative max-w-5xl mx-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8C532B]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca un capo…" data-testid="public-search" className="pl-9 bg-white" />
        </div>
        {data.categories.length > 0 && (
          <div className="flex gap-2 overflow-x-auto mt-2 pb-1 max-w-5xl mx-auto" data-testid="public-category-filter">
            <FilterChip active={catFilter === "all"} onClick={() => setCatFilter("all")} label="Tutti" />
            {data.categories.map((c) => <FilterChip key={c.id} active={catFilter === c.id} onClick={() => setCatFilter(c.id)} label={c.name} testid={`public-cat-${c.id}`} />)}
          </div>
        )}
      </div>

      <div className="px-4 py-4 max-w-5xl mx-auto space-y-6">
        {sections.length === 0 && <p className="text-sm text-[#8FA396]" data-testid="public-no-results">Nessun capo trovato.</p>}
        {sections.map((sec) => (
          <section key={sec.id} data-testid={`public-section-${sec.id}`}>
            <div className="flex items-center gap-3 mb-2">
              <h2 className="font-display text-lg font-bold text-[#1B3B2B] uppercase tracking-wide">{sec.name}</h2>
              <span className="flex-1 h-px bg-[#E5DEC9]" />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {sec.items.map((p) => <ProductCard key={p.id} product={p} token={token} onAdd={addToCart} onOpenGallery={openGallery} />)}
            </div>
          </section>
        ))}
      </div>

      {cartItems.length > 0 && (
        <div className="fixed bottom-0 inset-x-0 z-40 bg-white border-t border-[#E5DEC9] p-3 pb-safe" data-testid="public-cart-bar">
          <div className="max-w-3xl mx-auto flex items-center justify-between gap-3">
            <div>
              <p className="text-xs text-[#5F6B64]">{cartItems.reduce((s, [, it]) => s + it.qty, 0)} articoli</p>
              <p className="font-display font-bold text-[#1B3B2B]" data-testid="public-cart-total">Totale prenotazione: {eur(total)}</p>
            </div>
            <Button onClick={() => setConfirmOpen(true)} data-testid="public-cart-open" className="bg-[#1B3B2B] hover:bg-[#142E1F]">Prenota</Button>
          </div>
        </div>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="public-confirm-dialog" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader><DialogTitle className="font-display">Conferma prenotazione</DialogTitle>
            <DialogDescription className="text-xs">Il prezzo è indicativo. Non è previsto alcun pagamento online: la scuderia ti contatterà.</DialogDescription></DialogHeader>
          <div className="space-y-2 mb-3">
            {cartItems.map(([key, it]) => (
              <div key={key} className="flex items-center justify-between gap-2 text-sm" data-testid={`public-cart-item-${key}`}>
                <span className="min-w-0"><span className="font-medium text-[#1B3B2B]">{it.product.name}</span> <span className="text-[11px] text-[#8C6D4F]">{[it.gender, it.color, it.size].filter(Boolean).join(" / ")}</span></span>
                <span className="flex items-center gap-1.5 shrink-0">
                  <button onClick={() => setQty(key, it.qty - 1)} disabled={it.qty <= 1} className="p-1 rounded border border-[#E5DEC9] disabled:opacity-40" data-testid={`public-qty-dec-${key}`}><Minus className="h-3 w-3" /></button>
                  <span className="w-6 text-center" data-testid={`public-qty-${key}`}>{it.qty}</span>
                  <button onClick={() => setQty(key, it.qty + 1)} className="p-1 rounded border border-[#E5DEC9]" data-testid={`public-qty-inc-${key}`}><Plus className="h-3 w-3" /></button>
                  <span className="w-16 text-right">{eur(it.product.price * it.qty)}</span>
                  <button onClick={() => removeItem(key)} className="p-1 rounded text-red-500 hover:bg-red-50" data-testid={`public-cart-remove-${key}`} aria-label="Rimuovi"><Trash2 className="h-4 w-4" /></button>
                </span>
              </div>
            ))}
            <p className="text-right font-display font-bold text-[#1B3B2B] pt-1 border-t border-[#E5DEC9]">Totale: {eur(total)}</p>
          </div>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nome</Label><Input value={form.first_name} onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))} data-testid="public-first-name" name="equiro-req-first" autoComplete="off" className="mt-1" /></div>
              <div><Label>Cognome</Label><Input value={form.last_name} onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))} data-testid="public-last-name" name="equiro-req-last" autoComplete="off" className="mt-1" /></div>
            </div>
            <div><Label>Telefono</Label><Input type="tel" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} data-testid="public-phone" name="equiro-req-phone" autoComplete="off" className="mt-1" placeholder="Es. 347 1234567" /></div>
            <p className="text-xs text-[#5F6B64]" data-testid="public-privacy-notice">
              Inviando la prenotazione, nome, cognome e telefono saranno trattati dalla scuderia per gestire il tuo ordine.{" "}
              <span data-testid="public-privacy-link" className="underline decoration-dotted opacity-70"><a href="/privacy" target="_blank" rel="noreferrer">Informativa privacy</a></span>.
            </p>
            <div><Label>Note (facoltative)</Label><Textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} rows={3} data-testid="public-note" className="mt-1" placeholder="Es. È un regalo, chiamare prima di ordinare…" /><p className="text-[10px] text-[#8FA396] text-right mt-0.5">{note.length}/500</p></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Annulla</Button>
            <Button onClick={submit} disabled={busy || cartItems.length === 0} data-testid="public-confirm-submit" className="bg-[#1B3B2B] hover:bg-[#142E1F]">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Conferma prenotazione</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!done} onOpenChange={(o) => !o && setDone(null)}>
        <DialogContent data-testid="public-done-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Check className="h-5 w-5 text-emerald-600" /> Prenotazione ricevuta</DialogTitle>
            <DialogDescription className="text-xs">Grazie! La scuderia ha ricevuto la tua prenotazione e ti contatterà.</DialogDescription></DialogHeader>
          {done && <p className="text-sm text-[#1B3B2B]">Totale prenotazione: <b>{eur(done.total)}</b></p>}
          <DialogFooter><Button onClick={() => setDone(null)} className="bg-[#1B3B2B] hover:bg-[#142E1F]">Chiudi</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <GalleryDialog gallery={gallery} onClose={() => setGallery(null)} onIndex={(i) => setGallery((g) => ({ ...g, index: i }))} />
    </div>
  );
}

function GalleryDialog({ gallery, onClose, onIndex }) {
  const n = gallery?.photos?.length || 0;
  const go = (d) => onIndex((gallery.index + d + n) % n);
  return (
    <Dialog open={!!gallery} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="p-2 sm:max-w-lg" data-testid="public-photo-preview" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader><DialogTitle className="font-display text-base px-2 pt-1 flex items-center justify-between">
          <span className="truncate">{gallery?.name}</span>{n > 1 && <span className="text-xs text-[#8C6D4F] font-normal shrink-0" data-testid="public-gallery-counter">{gallery.index + 1} / {n}</span>}
        </DialogTitle><DialogDescription className="sr-only">Anteprima foto prodotto</DialogDescription></DialogHeader>
        {gallery && (
          <div className="relative">
            <img src={gallery.photos[gallery.index]} alt={gallery.name} className="w-full h-auto max-h-[70vh] object-contain rounded-lg bg-[#F3EFE4]" data-testid="public-photo-preview-img" />
            {n > 1 && <>
              <button onClick={() => go(-1)} data-testid="public-gallery-prev" className="absolute left-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/90 border border-[#E5DEC9] flex items-center justify-center shadow"><ChevronLeft className="h-5 w-5 text-[#1B3B2B]" /></button>
              <button onClick={() => go(1)} data-testid="public-gallery-next" className="absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/90 border border-[#E5DEC9] flex items-center justify-center shadow"><ChevronRight className="h-5 w-5 text-[#1B3B2B]" /></button>
              <div className="flex justify-center gap-1 mt-2">{gallery.photos.map((_, i) => <span key={i} className={`h-1.5 rounded-full transition-all ${i === gallery.index ? "w-4 bg-[#1B3B2B]" : "w-1.5 bg-[#C9BBA0]"}`} />)}</div>
            </>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function FilterChip({ active, onClick, label, testid }) {
  return <button onClick={onClick} data-testid={testid} className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border ${active ? "bg-[#1B3B2B] text-white border-[#1B3B2B]" : "bg-white text-[#1B3B2B] border-[#E5DEC9]"}`}>{label}</button>;
}

function ProductCard({ product, token, onAdd, onOpenGallery }) {
  const photos = photoList(token, product);
  const cover = photos[0] || null;
  const colors = product.colors || [];
  const fits = product.gender_options || [];
  const sizes = product.sizes || [];
  const [color, setColor] = useState(colors.length === 1 ? colors[0] : "");
  const [gender, setGender] = useState(fits.length === 1 ? fits[0] : "");
  const [size, setSize] = useState(sizes.length === 1 ? sizes[0] : "");
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [showDesc, setShowDesc] = useState(false);

  const needC = colors.length >= 2, needG = fits.length >= 2, needS = sizes.length >= 2;
  const ready = (!needC || color) && (!needG || gender) && (!needS || size);

  const props = [];
  if (colors.length === 1) props.push(colors[0]);
  if (fits.length === 1) props.push(fits[0]);
  if (sizes.length === 1) props.push(/^unica$/i.test(sizes[0]) ? "Taglia unica" : `Taglia ${sizes[0]}`);

  const add = () => {
    onAdd(product, { gender: gender || null, size: size || null, color: color || null }, qty);
    setAdded(true); setTimeout(() => setAdded(false), 1400); setQty(1);
  };

  const longDesc = (product.description || "").length > 90;
  return (
    <div className="rounded-xl border border-[#E5DEC9] bg-white p-3 flex gap-3" data-testid={`public-product-${product.id}`}>
      <button type="button" onClick={() => cover && onOpenGallery(product, 0)} data-testid={`public-photo-${product.id}`}
        className="relative shrink-0 h-24 w-24 sm:h-28 sm:w-28 rounded-lg bg-[#F3EFE4] overflow-hidden flex items-center justify-center">
        {cover ? <img src={cover} alt={product.name} loading="lazy" decoding="async" className="w-full h-full object-cover" /> : <ShoppingBag className="h-8 w-8 text-[#C9BBA0]" />}
        {photos.length > 1 && <span className="absolute bottom-1 right-1 inline-flex items-center gap-0.5 rounded-full bg-black/60 text-white text-[10px] px-1.5 py-0.5" data-testid={`public-photo-count-${product.id}`}><Images className="h-2.5 w-2.5" /> +{photos.length - 1}</span>}
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className="font-medium text-[#1B3B2B] leading-tight min-w-0">{product.name}</p>
          <p className="font-display font-bold text-[#1B3B2B] shrink-0">{eur(product.price)}</p>
        </div>
        {props.length > 0 && <p className="text-xs text-[#8C6D4F] mt-0.5" data-testid={`public-props-${product.id}`}>{props.join(" · ")}</p>}
        {product.description && (
          <div className="mt-0.5">
            <p className={`text-[11px] text-[#8FA396] ${showDesc ? "" : "line-clamp-1"}`}>{product.description}</p>
            {longDesc && <button onClick={() => setShowDesc((v) => !v)} className="text-[11px] text-[#8C532B] underline" data-testid={`public-desc-toggle-${product.id}`}>{showDesc ? "Nascondi" : "Dettagli"}</button>}
          </div>
        )}
        {needC && <VariantRow label="Colore" options={colors} value={color} onChange={setColor} testid={`public-color-${product.id}`} />}
        {needG && <VariantRow label="Vestibilità" options={fits} value={gender} onChange={setGender} testid={`public-gender-${product.id}`} />}
        {needS && <VariantRow label="Taglia" options={sizes} value={size} onChange={setSize} testid={`public-size-${product.id}`} />}
        <div className="flex items-center justify-between gap-2 mt-2 flex-wrap">
          <div className="flex items-center gap-1.5">
            <button onClick={() => setQty((v) => Math.max(1, v - 1))} data-testid={`public-qty-dec-${product.id}`} className="h-8 w-8 rounded-full border border-[#E5DEC9] flex items-center justify-center text-[#1B3B2B]"><Minus className="h-4 w-4" /></button>
            <span className="w-6 text-center font-medium text-[#1B3B2B]" data-testid={`public-qty-${product.id}`}>{qty}</span>
            <button onClick={() => setQty((v) => Math.min(99, v + 1))} data-testid={`public-qty-inc-${product.id}`} className="h-8 w-8 rounded-full border border-[#E5DEC9] flex items-center justify-center text-[#1B3B2B]"><Plus className="h-4 w-4" /></button>
          </div>
          <Button onClick={add} disabled={!ready} data-testid={`public-add-${product.id}`} className={`h-9 ${added ? "bg-emerald-600 hover:bg-emerald-600" : "bg-[#1B3B2B] hover:bg-[#142E1F]"} disabled:opacity-50`}>
            {added ? <><Check className="h-4 w-4 mr-1" /> Aggiunto</> : <><Plus className="h-4 w-4 mr-1" /> Aggiungi</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

function VariantRow({ label, options, value, onChange, testid }) {
  return (
    <div className="mt-1.5">
      <p className="text-[10px] text-[#8C532B] uppercase tracking-wide mb-1">{label}</p>
      <div className="flex flex-wrap gap-1.5">
        {options.map((o) => (
          <button key={o} onClick={() => onChange(value === o ? "" : o)} data-testid={`${testid}-${o}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border ${value === o ? "bg-[#8C532B] text-white border-[#8C532B]" : "bg-white text-[#1B3B2B] border-[#E5DEC9]"}`}>{o}</button>
        ))}
      </div>
    </div>
  );
}
